import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Popover, Spin, Tooltip } from 'antd';
import { FC, ReactNode } from 'react';

/** Mobile breakpoint — at or below this we show tap-to-reveal info icons */
const MOBILE_BREAKPOINT = 770;

export interface IRecruitmentGoalCardProps {
  /** Card title (e.g. "QUARTERLY RECRUITMENT") */
  title: string;
  /** Units collected so far (pRBC count) */
  unitsCollected: number;
  /** Goal for the period */
  goal: number;
  /** Label for the goal column (e.g. "Quarterly goal") */
  goalLabel?: string;
  /** Tooltip text explaining how the goal is calculated */
  goalTooltip?: string;
  /** Progress percentage (0-100) */
  progressPercent: number;
  /** Status info from getStatusFromBreakpoints() */
  statusInfo: { color: string; label: string } | null;
  /** Tooltip text explaining the progress status label */
  statusTooltip?: string;
  /** Daily pace needed to hit goal */
  dailyPaceNeeded?: number;
  /** Daily pace label text */
  dailyPaceLabel?: string;
  /** Font size for daily pace */
  dailyPaceFontSize?: number;
  /** Footer text (e.g. "Across all offices") */
  footerText?: string;
  /** Show decorative elements (dots, crosses) — default true */
  showDecorations?: boolean;
  /** Click handler for units collected value */
  onUnitsClick?: () => void;
  /** Tooltip for units click */
  unitsClickTooltip?: string;
  /** Primary accent color */
  accentColor?: string;
  /** Label for the units column (default: "Units collected") */
  unitsLabel?: string;
  /** Secondary metric value (e.g. FFP count) — shown to the right of units */
  secondaryValue?: number;
  /** Label for secondary metric (e.g. "FFP Collected") */
  secondaryLabel?: string;
  /** Click handler for secondary value */
  onSecondaryClick?: () => void;
  /** Tooltip for secondary click */
  secondaryClickTooltip?: string;
  /** Whether data is loading (shows overlay spinner) */
  loading?: boolean;
  /** Days elapsed in the period (for days progress bar) */
  daysElapsed?: number;
  /** Total days in the period (for days progress bar) */
  totalDays?: number;
  /** Current window width — used to switch between hover tooltips and tap popovers */
  windowWidth?: number;
}

/** Decorative scattered elements (dots and crosses) */
const Decorations: FC = () => (
  <>
    {/* Top-left area */}
    <svg
      style={{ position: 'absolute', top: 8, left: 12, opacity: 0.35 }}
      width="10"
      height="10"
    >
      <circle cx="5" cy="5" r="4" fill="#a8c4e0" />
    </svg>
    <svg
      style={{ position: 'absolute', top: 30, left: 30, opacity: 0.2 }}
      width="6"
      height="6"
    >
      <circle cx="3" cy="3" r="3" fill="#e8a0a0" />
    </svg>

    {/* Top-right area */}
    <span
      style={{
        position: 'absolute',
        top: 10,
        right: 18,
        fontSize: 14,
        color: '#e08080',
        opacity: 0.45,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      +
    </span>
    <svg
      style={{ position: 'absolute', top: 35, right: 35, opacity: 0.2 }}
      width="6"
      height="6"
    >
      <circle cx="3" cy="3" r="3" fill="#a8c4e0" />
    </svg>

    {/* Bottom-left area */}
    <span
      style={{
        position: 'absolute',
        bottom: 50,
        left: 15,
        fontSize: 12,
        color: '#a8c4e0',
        opacity: 0.35,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      +
    </span>
    <svg
      style={{ position: 'absolute', bottom: 70, left: 35, opacity: 0.2 }}
      width="5"
      height="5"
    >
      <circle cx="2.5" cy="2.5" r="2.5" fill="#e8a0a0" />
    </svg>

    {/* Bottom-right area */}
    <span
      style={{
        position: 'absolute',
        bottom: 55,
        right: 20,
        fontSize: 14,
        color: '#e08080',
        opacity: 0.4,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      +
    </span>
    <svg
      style={{ position: 'absolute', bottom: 75, right: 40, opacity: 0.25 }}
      width="6"
      height="6"
    >
      <circle cx="3" cy="3" r="3" fill="#a8c4e0" />
    </svg>

    {/* Mid-left */}
    <svg
      style={{ position: 'absolute', top: '45%', left: 8, opacity: 0.2 }}
      width="5"
      height="5"
    >
      <circle cx="2.5" cy="2.5" r="2.5" fill="#c0d4e8" />
    </svg>

    {/* Mid-right */}
    <svg
      style={{ position: 'absolute', top: '40%', right: 10, opacity: 0.2 }}
      width="5"
      height="5"
    >
      <circle cx="2.5" cy="2.5" r="2.5" fill="#e0b0b0" />
    </svg>
  </>
);

/** Lighten a status color for use on dark backgrounds */
function lightenStatusColor(color: string): string {
  const map: Record<string, string> = {
    '#f31a25': '#ff6b6b', // red → light coral
    '#faad14': '#ffe066', // yellow/orange → light gold
    '#389e0d': '#73d13d', // green → light green
  };
  return map[color.toLowerCase()] ?? color;
}

/** Status icon based on status label */
function getStatusIcon(label: string): ReactNode {
  const lower = label.toLowerCase();
  if (
    lower.includes('track') ||
    lower.includes('good') ||
    lower.includes('high')
  ) {
    return <CheckCircleFilled style={{ marginRight: 6 }} />;
  }
  if (lower.includes('risk') || lower.includes('mid') || lower.includes('ok')) {
    return <WarningFilled style={{ marginRight: 6 }} />;
  }
  return <ExclamationCircleFilled style={{ marginRight: 6 }} />;
}

/**
 * On mobile (≤768px): renders a tappable InfoCircleOutlined icon inline (for text labels).
 * On desktop: renders nothing — the parent element should be wrapped with a Tooltip instead.
 */
const MobileInfoIcon: FC<{ text: string; color?: string }> = ({
  text,
  color = '#8c8c8c',
}) => (
  <Popover
    content={
      <span style={{ maxWidth: 220, display: 'block', color: '#e8ecef' }}>
        {text}
      </span>
    }
    trigger="click"
    placement="bottom"
    overlayStyle={{ maxWidth: 240 }}
    overlayInnerStyle={{ backgroundColor: '#0b345f', color: '#e8ecef' }}
  >
    <InfoCircleOutlined
      style={{ fontSize: 12, color, cursor: 'pointer', flexShrink: 0 }}
    />
  </Popover>
);

/**
 * On mobile (≤768px): renders as a split-tab extension of a button/badge — visually merges
 * with the left element via negative margin and matching border/background.
 * Used for button-like badge elements (pace badge, status badge).
 */
const MobileSplitTabIcon: FC<{
  text: string;
  bgColor: string;
  borderColor: string;
}> = ({ text, bgColor, borderColor }) => (
  <Popover
    content={
      <span style={{ maxWidth: 220, display: 'block', color: '#e8ecef' }}>
        {text}
      </span>
    }
    trigger="click"
    placement="bottom"
    overlayStyle={{ maxWidth: 240 }}
    overlayInnerStyle={{ backgroundColor: '#0b345f', color: '#e8ecef' }}
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'stretch',
        width: 28,
        marginLeft: -1,
        borderTop: `1px solid ${borderColor}`,
        borderRight: `1px solid ${borderColor}`,
        borderBottom: `1px solid ${borderColor}`,
        borderLeft: '1px solid rgba(255,255,255,0.6)',
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        backgroundColor: bgColor,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <InfoCircleOutlined style={{ color: '#e8ecef', fontSize: 13 }} />
    </span>
  </Popover>
);

export const RecruitmentGoalCard: FC<IRecruitmentGoalCardProps> = ({
  title,
  unitsCollected,
  goal,
  goalLabel = 'Quarterly goal',
  goalTooltip,
  progressPercent,
  statusInfo,
  statusTooltip,
  dailyPaceNeeded,
  dailyPaceLabel = 'Daily pace needed to hit goal:',
  dailyPaceFontSize = 15,
  footerText = 'Across all offices',
  showDecorations = true,
  unitsLabel = 'Units collected',
  onUnitsClick,
  unitsClickTooltip,
  secondaryValue,
  secondaryLabel,
  onSecondaryClick,
  secondaryClickTooltip,
  accentColor = '#0b345f',
  loading = false,
  daysElapsed,
  totalDays,
  windowWidth,
}) => {
  const isMobile = (windowWidth ?? window.innerWidth) <= MOBILE_BREAKPOINT;

  const unitsRemaining = Math.max(0, goal - unitsCollected);
  const statusColor = statusInfo?.color ?? '#389e0d';
  const barFillColor = unitsCollected === 0 ? '#d9534f' : statusColor;

  const isPeriodClosedPace = dailyPaceNeeded === 0;

  const progressTooltipText = `This is the percentage of the period goal already collected. ${progressPercent}% means ${unitsCollected.toLocaleString()} of the ${goal.toLocaleString()}-unit goal have been collected so far.`;
  const unitsBarTooltipText =
    'Units bar: shows pRBC donations collected vs. the period goal. The bar fills as units are collected — color reflects current pace status.';
  const daysBarTooltipText =
    daysElapsed !== undefined && totalDays !== undefined
      ? `Days bar: shows working days elapsed vs. total working days in the period. ${daysElapsed} of ${totalDays} working days have passed. This bar helps you gauge how much time is left to hit the goal.`
      : '';
  const paceTooltipText = isPeriodClosedPace
    ? '∞ is shown when the selected period has already ended — there are no remaining working days to collect units, so no pace can be calculated.'
    : `Daily pace = units still needed ÷ working days remaining. You need ${dailyPaceNeeded} unit${
        dailyPaceNeeded === 1 ? '' : 's'
      } per working day to reach the goal by end of period. Goal is calculated as: daily pRBC target × working days in period × number of offices.`;

  const unitsValue = (
    <span
      style={{
        fontSize: 36,
        fontWeight: 700,
        color: '#2f3a45',
        lineHeight: 1,
        cursor: onUnitsClick ? 'pointer' : undefined,
        textDecoration: onUnitsClick ? 'underline' : undefined,
      }}
      onClick={onUnitsClick}
    >
      {unitsCollected.toLocaleString()}
    </span>
  );

  // Progress % label — desktop wraps span with Tooltip, mobile appends icon
  const progressLabel = isMobile ? (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: '#8c8c8c',
        whiteSpace: 'nowrap',
      }}
    >
      Progress: {progressPercent}%
      <MobileInfoIcon text={progressTooltipText} />
    </span>
  ) : (
    <Tooltip title={progressTooltipText} placement="top">
      <span
        style={{
          fontSize: 12,
          color: '#8c8c8c',
          whiteSpace: 'nowrap',
          cursor: 'default',
        }}
      >
        Progress: {progressPercent}%
      </span>
    </Tooltip>
  );

  // Goal label — desktop wraps with Tooltip, mobile appends icon
  const goalLabelNode = goalTooltip ? (
    isMobile ? (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 13,
          color: '#8c8c8c',
        }}
      >
        🎯 {goalLabel}
        <MobileInfoIcon text={goalTooltip} />
      </span>
    ) : (
      <Tooltip title={goalTooltip} placement="top">
        <span style={{ fontSize: 13, color: '#8c8c8c', cursor: 'default' }}>
          🎯 {goalLabel}
        </span>
      </Tooltip>
    )
  ) : (
    <span style={{ fontSize: 13, color: '#8c8c8c' }}>🎯 {goalLabel}</span>
  );

  // Units bar label
  const unitsBarLabel = isMobile ? (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 13,
        color: '#595959',
        whiteSpace: 'nowrap',
        fontWeight: 600,
        minWidth: 110,
        textAlign: 'right',
      }}
    >
      {unitsCollected.toLocaleString()} / {goal.toLocaleString()} units
      <MobileInfoIcon text={unitsBarTooltipText} />
    </span>
  ) : (
    <Tooltip title={unitsBarTooltipText} placement="top">
      <span
        style={{
          fontSize: 13,
          color: '#595959',
          whiteSpace: 'nowrap',
          fontWeight: 600,
          minWidth: 110,
          textAlign: 'right',
          cursor: 'default',
        }}
      >
        {unitsCollected.toLocaleString()} / {goal.toLocaleString()} units
      </span>
    </Tooltip>
  );

  // Days bar label
  const daysBarLabel =
    daysElapsed !== undefined && totalDays !== undefined && totalDays > 0 ? (
      isMobile ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            color: '#595959',
            whiteSpace: 'nowrap',
            fontWeight: 600,
            minWidth: 110,
            textAlign: 'right',
          }}
        >
          {daysElapsed} / {totalDays} days
          <MobileInfoIcon text={daysBarTooltipText} />
        </span>
      ) : (
        <Tooltip title={daysBarTooltipText} placement="top">
          <span
            style={{
              fontSize: 13,
              color: '#595959',
              whiteSpace: 'nowrap',
              fontWeight: 600,
              minWidth: 110,
              textAlign: 'right',
              cursor: 'default',
            }}
          >
            {daysElapsed} / {totalDays} days
          </span>
        </Tooltip>
      )
    ) : null;

  // Status badge
  const statusBadge = statusInfo ? (
    isMobile && statusTooltip ? (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: `${statusInfo.color}18`,
            border: `1px solid ${statusInfo.color}40`,
            color: statusInfo.color,
            fontSize: 13,
            fontWeight: 600,
            padding: '5px 14px',
            borderRadius: '20px 0 0 20px',
            gap: 4,
          }}
        >
          {getStatusIcon(statusInfo.label)}
          Progress: {statusInfo.label}
        </span>
        <MobileSplitTabIcon
          text={statusTooltip}
          bgColor={statusInfo.color}
          borderColor={statusInfo.color}
        />
      </span>
    ) : statusTooltip ? (
      <Tooltip title={statusTooltip} placement="top">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: `${statusInfo.color}18`,
            border: `1px solid ${statusInfo.color}40`,
            color: statusInfo.color,
            fontSize: 13,
            fontWeight: 600,
            padding: '5px 14px',
            borderRadius: 20,
            gap: 4,
            cursor: 'default',
          }}
        >
          {getStatusIcon(statusInfo.label)}
          Progress: {statusInfo.label}
        </span>
      </Tooltip>
    ) : (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: `${statusInfo.color}18`,
          border: `1px solid ${statusInfo.color}40`,
          color: statusInfo.color,
          fontSize: 13,
          fontWeight: 600,
          padding: '5px 14px',
          borderRadius: 20,
          gap: 4,
        }}
      >
        {getStatusIcon(statusInfo.label)}
        Progress: {statusInfo.label}
      </span>
    )
  ) : null;

  // Daily pace badge inner content
  const paceInner = (
    <>
      {dailyPaceLabel}{' '}
      <strong
        style={{
          fontSize: '140%',
          color: statusInfo ? lightenStatusColor(statusInfo.color) : '#fff',
        }}
      >
        {dailyPaceNeeded !== undefined && dailyPaceNeeded > 0
          ? dailyPaceNeeded
          : '\u221e'}
      </strong>
    </>
  );

  const paceBadge =
    dailyPaceNeeded !== undefined ? (
      isMobile ? (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: dailyPaceFontSize,
              fontWeight: 600,
              backgroundColor: accentColor,
              borderRadius: '4px 0 0 4px',
              padding: '4px 10px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
              color: '#fff',
            }}
          >
            {paceInner}
          </div>
          <MobileSplitTabIcon
            text={paceTooltipText}
            bgColor={accentColor}
            borderColor={accentColor}
          />
        </span>
      ) : (
        <Tooltip title={paceTooltipText} placement="top">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: dailyPaceFontSize,
              fontWeight: 600,
              backgroundColor: accentColor,
              borderRadius: 4,
              padding: '4px 10px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
              color: '#fff',
              cursor: 'default',
            }}
          >
            {paceInner}
          </div>
        </Tooltip>
      )
    ) : null;

  return (
    <div
      style={{
        position: 'relative',
        padding: '20px 24px',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      {showDecorations && <Decorations />}

      {/* Loading overlay */}
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 'inherit',
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
        </div>
      )}

      {/* Title */}
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: '#2f3a45',
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        {title}
      </div>

      {/* Progress line with percentage */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1, height: 1, background: '#d9d9d9' }} />
        {progressLabel}
        <div style={{ flex: 1, height: 1, background: '#d9d9d9' }} />
      </div>

      {/* Stats box */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          marginBottom: 16,
        }}
      >
        {/* Units collected */}
        <div
          style={{
            flex: 1,
            padding: '16px 12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: '#8c8c8c',
            }}
          >
            <svg width="14" height="18" viewBox="0 0 14 18">
              <path
                d="M7 0C7 0 0 7.5 0 11.5C0 15 3.13 18 7 18C10.87 18 14 15 14 11.5C14 7.5 7 0 7 0Z"
                fill="#c8a2d4"
                opacity={0.7}
              />
            </svg>
            {unitsLabel}
          </div>
          {onUnitsClick && unitsClickTooltip ? (
            <Tooltip
              title={unitsClickTooltip}
              overlayInnerStyle={{
                background: '#2f3a45',
                border: '1px solid #0b345f',
                borderRadius: 3,
              }}
              color="#2f3a45"
            >
              {unitsValue}
            </Tooltip>
          ) : (
            unitsValue
          )}
        </div>

        {/* Secondary metric (e.g. FFP) */}
        {secondaryValue !== undefined && secondaryLabel && (
          <>
            <div
              style={{ width: 1, background: '#e8e8e8', margin: '12px 0' }}
            />
            <div
              style={{
                flex: 1,
                padding: '16px 12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  color: '#8c8c8c',
                }}
              >
                <svg width="14" height="18" viewBox="0 0 14 18">
                  <path
                    d="M7 0C7 0 0 7.5 0 11.5C0 15 3.13 18 7 18C10.87 18 14 15 14 11.5C14 7.5 7 0 7 0Z"
                    fill="#a8c4e0"
                    opacity={0.7}
                  />
                </svg>
                {secondaryLabel}
              </div>
              {onSecondaryClick && secondaryClickTooltip ? (
                <Tooltip
                  title={secondaryClickTooltip}
                  overlayInnerStyle={{
                    background: '#2f3a45',
                    border: '1px solid #0b345f',
                    borderRadius: 3,
                  }}
                  color="#2f3a45"
                >
                  <span
                    style={{
                      fontSize: 36,
                      fontWeight: 700,
                      color: '#2f3a45',
                      lineHeight: 1,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={onSecondaryClick}
                  >
                    {secondaryValue.toLocaleString()}
                  </span>
                </Tooltip>
              ) : (
                <span
                  style={{
                    fontSize: 36,
                    fontWeight: 700,
                    color: '#2f3a45',
                    lineHeight: 1,
                  }}
                >
                  {secondaryValue.toLocaleString()}
                </span>
              )}
            </div>
          </>
        )}

        {/* Divider */}
        <div style={{ width: 1, background: '#e8e8e8', margin: '12px 0' }} />

        {/* Goal */}
        <div
          style={{
            flex: 1,
            padding: '16px 12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {goalLabelNode}
          <span
            style={{
              fontSize: 36,
              fontWeight: 700,
              color: '#2f3a45',
              lineHeight: 1,
            }}
          >
            {goal.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Progress bars */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 16,
        }}
      >
        {/* Units bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Tooltip
            title={!isMobile ? unitsBarTooltipText : undefined}
            placement="top"
          >
            <div
              style={{
                flex: 1,
                height: 18,
                backgroundColor: '#e8e8e8',
                borderRadius: 9,
                overflow: 'hidden',
                cursor: !isMobile ? 'default' : undefined,
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                  minWidth: progressPercent > 0 ? 18 : 0,
                  height: '100%',
                  backgroundColor: barFillColor,
                  borderRadius: 9,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </Tooltip>
          {unitsBarLabel}
        </div>

        {/* Days bar */}
        {daysElapsed !== undefined &&
          totalDays !== undefined &&
          totalDays > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Tooltip
                title={!isMobile ? daysBarTooltipText : undefined}
                placement="top"
              >
                <div
                  style={{
                    flex: 1,
                    height: 18,
                    backgroundColor: '#e8e8e8',
                    borderRadius: 9,
                    overflow: 'hidden',
                    cursor: !isMobile ? 'default' : undefined,
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(0, (daysElapsed / totalDays) * 100)
                      )}%`,
                      minWidth: daysElapsed > 0 ? 18 : 0,
                      height: '100%',
                      backgroundColor: '#1565c0',
                      borderRadius: 9,
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
              </Tooltip>
              {daysBarLabel}
            </div>
          )}
      </div>

      {/* Units remaining callout */}
      <div style={{ marginBottom: 12, textAlign: 'center' }}>
        <div
          style={{
            fontSize: 42,
            fontWeight: 800,
            color: statusColor,
            lineHeight: 1,
          }}
        >
          {unitsRemaining.toLocaleString()}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#595959',
            marginTop: 4,
          }}
        >
          pRBC Units Remaining
        </div>
      </div>

      {/* Status badge */}
      {statusBadge && <div style={{ marginBottom: 12 }}>{statusBadge}</div>}

      {/* Footer */}
      {footerText && (
        <div
          style={{
            fontSize: 12,
            color: '#8c8c8c',
            marginBottom: dailyPaceNeeded !== undefined ? 12 : 0,
          }}
        >
          {footerText}
        </div>
      )}

      {/* Daily pace badge */}
      {paceBadge && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {paceBadge}
        </div>
      )}
    </div>
  );
};

export default RecruitmentGoalCard;
